'use client';

import type { UseFormReturn } from 'react-hook-form';

import { Autocomplete, type AutocompleteOption } from '@kit/ui/autocomplete';
import { Checkbox } from '@kit/ui/checkbox';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@kit/ui/form';
import { GenderSelector } from '@kit/ui/gender-selector';
import { Label } from '@kit/ui/label';
import { RadioGroup, RadioGroupItem } from '@kit/ui/radio-group';

import type {
  ApplicationFieldConfig,
  FieldOption,
} from '../../types/audition.types';
import {
  buildRequiredRule,
  resolveOptions,
} from './dynamic-form-renderer-shared';

type Form = UseFormReturn<Record<string, unknown>>;

/**
 * Shared multi-select field for `checkboxGroup` / `multiselect` structure
 * fields. Wraps the canonical `@kit/ui/autocomplete` primitive in `multiple`
 * mode (type-to-filter inline) and surfaces the current selection as removable
 * badges below the field, replacing the previous checkbox grid.
 */
export function MultiSelectAutocomplete({
  options,
  value,
  onChange,
  placeholder,
}: {
  options: FieldOption[];
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
}) {
  const acOptions: AutocompleteOption[] = options.map((option) => ({
    value: option.value,
    label: option.label,
  }));

  // The primitive renders selected values as removable chips inside the field,
  // so no separate badge strip is needed here.
  return (
    <Autocomplete
      multiple
      clearOnSelect
      value={value ?? []}
      onChange={(next) => onChange(Array.isArray(next) ? next : [])}
      options={acOptions}
      placeholder={placeholder}
    />
  );
}

export function SelectField({
  field,
  form,
  selectPlaceholder,
  requiredMessage,
  dynamicOptions,
}: {
  field: ApplicationFieldConfig;
  form: Form;
  selectPlaceholder: string;
  requiredMessage: string;
  dynamicOptions?: Record<string, FieldOption[]>;
}): React.ReactNode {
  return (
    <FormField
      control={form.control}
      name={field.key}
      rules={buildRequiredRule(field, requiredMessage)}
      render={({ field: formField }) => (
        <FormItem>
          <FormLabel>
            {field.label}
            {field.validation?.required && (
              <span className="text-destructive ml-1">*</span>
            )}
          </FormLabel>
          <FormControl>
            <Autocomplete
              value={(formField.value as string) || undefined}
              onChange={(next) =>
                formField.onChange(typeof next === 'string' ? next : '')
              }
              options={resolveOptions(field, dynamicOptions).map(
                (option): AutocompleteOption => ({
                  value: option.value,
                  label: option.label,
                }),
              )}
              placeholder={field.placeholder || selectPlaceholder}
            />
          </FormControl>
          {field.description && (
            <FormDescription>{field.description}</FormDescription>
          )}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

export function MultiselectField({
  field,
  form,
  selectPlaceholder,
  requiredMessage,
  dynamicOptions,
}: {
  field: ApplicationFieldConfig;
  form: Form;
  selectPlaceholder: string;
  requiredMessage: string;
  dynamicOptions?: Record<string, FieldOption[]>;
}): React.ReactNode {
  return (
    <FormField
      control={form.control}
      name={field.key}
      rules={buildRequiredRule(field, requiredMessage)}
      render={({ field: formField }) => (
        <FormItem>
          <FormLabel>
            {field.label}
            {field.validation?.required && (
              <span className="text-destructive ml-1">*</span>
            )}
          </FormLabel>
          {field.description && (
            <FormDescription>{field.description}</FormDescription>
          )}
          <FormControl>
            <MultiSelectAutocomplete
              options={resolveOptions(field, dynamicOptions)}
              value={(formField.value as string[]) || []}
              onChange={formField.onChange}
              placeholder={field.placeholder || selectPlaceholder}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

export function CheckboxGroupField({
  field,
  form,
  requiredMessage,
  dynamicOptions,
}: {
  field: ApplicationFieldConfig;
  form: Form;
  requiredMessage: string;
  dynamicOptions?: Record<string, FieldOption[]>;
}): React.ReactNode {
  // A multi-value question rendered as a vertical list of literal
  // checkboxes (the field-type name's intent) — not a dropdown. The value
  // is the array of selected option values; toggling a box adds/removes it.
  const groupOptions = resolveOptions(field, dynamicOptions);
  return (
    <FormField
      control={form.control}
      name={field.key}
      rules={buildRequiredRule(field, requiredMessage)}
      render={({ field: formField }) => {
        const selected = (formField.value as string[]) || [];
        return (
          <FormItem>
            <FormLabel>
              {field.label}
              {field.validation?.required && (
                <span className="text-destructive ml-1">*</span>
              )}
            </FormLabel>
            {field.description && (
              <FormDescription>{field.description}</FormDescription>
            )}
            <div className="space-y-2">
              {groupOptions.map((option) => (
                <label
                  key={option.value}
                  className="flex cursor-pointer flex-row items-center space-x-3 rounded-md border p-4"
                >
                  <Checkbox
                    checked={selected.includes(option.value)}
                    onCheckedChange={(next) =>
                      formField.onChange(
                        next === true
                          ? [...selected, option.value]
                          : selected.filter((v) => v !== option.value),
                      )
                    }
                  />
                  <span className="text-sm leading-none font-medium">
                    {option.label}
                  </span>
                </label>
              ))}
            </div>
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
}

export function RadioField({
  field,
  form,
  requiredMessage,
  dynamicOptions,
}: {
  field: ApplicationFieldConfig;
  form: Form;
  requiredMessage: string;
  dynamicOptions?: Record<string, FieldOption[]>;
}): React.ReactNode {
  return (
    <FormField
      control={form.control}
      name={field.key}
      rules={buildRequiredRule(field, requiredMessage)}
      render={({ field: formField }) => (
        <FormItem>
          <FormLabel>
            {field.label}
            {field.validation?.required && (
              <span className="text-destructive ml-1">*</span>
            )}
          </FormLabel>
          {field.description && (
            <FormDescription>{field.description}</FormDescription>
          )}
          <FormControl>
            <RadioGroup
              value={(formField.value as string) || ''}
              onValueChange={formField.onChange}
              className="grid gap-2 pt-2 sm:grid-cols-2"
            >
              {resolveOptions(field, dynamicOptions).map((option) => (
                <div key={option.value} className="flex items-center space-x-2">
                  <RadioGroupItem
                    value={option.value}
                    id={`${field.key}-${option.value}`}
                  />
                  <Label
                    htmlFor={`${field.key}-${option.value}`}
                    className="cursor-pointer text-sm font-normal"
                  >
                    {option.label}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

/**
 * Gender renders as a segmented pill selector (best practice for a short,
 * mutually-exclusive enum — not a dropdown). Detected by key so it also
 * applies to stored structures; legacy 3-option structures are filtered to
 * the supported binary set (existing non-binary values stay in the DB, they
 * are just not re-selectable here).
 */
export function GenderField({
  field,
  form,
  requiredMessage,
  dynamicOptions,
}: {
  field: ApplicationFieldConfig;
  form: Form;
  requiredMessage: string;
  dynamicOptions?: Record<string, FieldOption[]>;
}): React.ReactNode {
  const genderOptions = resolveOptions(field, dynamicOptions)
    .filter((option) => option.value === 'male' || option.value === 'female')
    .map((option) => ({ value: option.value, label: option.label }));

  return (
    <FormField
      control={form.control}
      name={field.key}
      rules={buildRequiredRule(field, requiredMessage)}
      render={({ field: formField }) => (
        <FormItem>
          <FormLabel>
            {field.label}
            {field.validation?.required && (
              <span className="text-destructive ml-1">*</span>
            )}
          </FormLabel>
          {field.description && (
            <FormDescription>{field.description}</FormDescription>
          )}
          <FormControl>
            <GenderSelector
              value={(formField.value as string) ?? ''}
              onChange={formField.onChange}
              options={genderOptions.length > 0 ? genderOptions : undefined}
              aria-label={field.label}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

/**
 * Country-backed choice field. Delegates to the consumer's autocomplete picker
 * slot (the dataset lives in @kit/artists). Covers both `select` (single,
 * residence) and `multiselect` (multi, nationalities).
 */
export function CountryChoiceField({
  field,
  form,
  requiredMessage,
  renderCountryField,
}: {
  field: ApplicationFieldConfig;
  form: Form;
  requiredMessage: string;
  renderCountryField: (args: {
    field: ApplicationFieldConfig;
    value: string | string[];
    onChange: (value: string | string[]) => void;
    multiple: boolean;
  }) => React.ReactNode;
}): React.ReactNode {
  const multiple = field.type === 'multiselect';
  return (
    <FormField
      control={form.control}
      name={field.key}
      rules={buildRequiredRule(field, requiredMessage)}
      render={({ field: formField }) => (
        <FormItem>
          <FormLabel>
            {field.label}
            {field.validation?.required && (
              <span className="text-destructive ml-1">*</span>
            )}
          </FormLabel>
          {field.description && (
            <FormDescription>{field.description}</FormDescription>
          )}
          <FormControl>
            <div>
              {renderCountryField({
                field,
                value: multiple
                  ? Array.isArray(formField.value)
                    ? (formField.value as string[])
                    : []
                  : ((formField.value as string) ?? ''),
                onChange: formField.onChange,
                multiple,
              })}
            </div>
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

/**
 * Delegated to the consumer-injected slot (the StyleEntryPicker, sourced
 * from the competition's competition_categories). The slot is wired to
 * the form value (an array of selected style-entry ids).
 */
export function CompetitionStylesField({
  field,
  form,
  requiredMessage,
  renderStylesField,
}: {
  field: ApplicationFieldConfig;
  form: Form;
  requiredMessage: string;
  renderStylesField: (args: {
    field: ApplicationFieldConfig;
    value: string[];
    onChange: (ids: string[]) => void;
  }) => React.ReactNode;
}): React.ReactNode {
  return (
    <FormField
      control={form.control}
      name={field.key}
      rules={buildRequiredRule(field, requiredMessage)}
      render={({ field: formField }) => (
        <FormItem className="col-span-full">
          <FormLabel>
            {field.label}
            {field.validation?.required && (
              <span className="text-destructive ml-1">*</span>
            )}
          </FormLabel>
          {field.description && (
            <FormDescription>{field.description}</FormDescription>
          )}
          <FormControl>
            <div>
              {renderStylesField({
                field,
                value: Array.isArray(formField.value)
                  ? (formField.value as string[])
                  : [],
                onChange: formField.onChange,
              })}
            </div>
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
